/** Gate onboarding: abbonamento attivo + firma documenti prima dell'app operativa. */
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { endpoints } from "@/api/client";
import { CenteredLoader } from "@/components/ui/feedback";
import { useAuth } from "@/lib/AuthProvider";
import { clearAziendaSilently, getAziendaId } from "@/lib/azienda";

const BYPASS_PATHS = ["/abbonamento", "/impostazioni"];
const GESTORE_PATHS = ["/gestore", "/impostazioni"];

export function BillingGate() {
  const location = useLocation();
  const auth = useAuth();
  const path = location.pathname;

  const statusQ = useQuery({
    queryKey: ["billing-status"],
    queryFn: endpoints.billing.status,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  // La pagina Amministrazione non dipende dal piano dell'admin stesso.
  if (auth.isAdmin && !auth.impersonating && path.startsWith("/admin")) {
    return <Outlet />;
  }

  // Azienda selezionata ma non più usabile (rimossa o non tua): torna al gestore.
  const err = statusQ.error as { status?: number } | null;
  if (statusQ.isError && getAziendaId() && (err?.status === 403 || err?.status === 402)) {
    clearAziendaSilently();
    window.location.assign("/gestore");
    return <CenteredLoader />;
  }

  if (statusQ.isLoading) {
    return <CenteredLoader label="Verifica abbonamento…" />;
  }

  if (statusQ.isError) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-muted-foreground">Impossibile verificare lo stato del piano.</p>
        <button
          type="button"
          className="text-sm text-primary underline"
          onClick={() => statusQ.refetch()}
        >
          Riprova
        </button>
      </div>
    );
  }

  const s = statusQ.data;
  if (!s) return <CenteredLoader />;

  // Gestore sul proprio account (nessuna azienda selezionata): solo la pagina Le mie aziende
  // e le impostazioni; per operare deve entrare in un'azienda.
  if (s.is_gestore && !s.managed_by) {
    const allowed = GESTORE_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
    if (allowed) return <Outlet />;
    return <Navigate to="/gestore" replace />;
  }

  if (s.billing_disabled || s.can_use_app) {
    return <Outlet />;
  }

  const onBypass = BYPASS_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

  if (!s.subscription_active) {
    if (onBypass) return <Outlet />;
    return <Navigate to="/abbonamento" replace state={{ from: path }} />;
  }

  if (!s.signing.canUseApp) {
    if (path === "/abbonamento") return <Outlet />;
    return <Navigate to="/abbonamento?step=firma" replace />;
  }

  return <Outlet />;
}
